import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, BadgeCheck, MapPin, PlayCircle, Quote } from 'lucide-react';
import { formatFa, getGlobeCityLabel } from './constants';
import type { CompanionCity } from './types';

type CompanionsCityGridProps = {
  cities: CompanionCity[];
  activeCityId: string;
  onSelectCity: (cityId: string) => void;
  onOpenVideo: (city: CompanionCity) => void;
};

const CompanionsCityGrid: React.FC<CompanionsCityGridProps> = ({
  cities,
  activeCityId,
  onSelectCity,
  onOpenVideo,
}) => {
  if (cities.length === 0) {
    return (
      <div className="cp-stories-empty">
        <p>شهری برای این منطقه ثبت نشده است.</p>
      </div>
    );
  }

  return (
    <div className="cp-stories-grid">
      {cities.map((city, index) => {
        const selected = city.id === activeCityId;
        return (
          <motion.article
            key={city.id}
            layout
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.35, delay: Math.min(index * 0.04, 0.24) }}
            onClick={() => onSelectCity(city.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelectCity(city.id);
              }
            }}
            role="button"
            tabIndex={0}
            aria-pressed={selected}
            className={`cp-stories-card group ${selected ? 'cp-stories-card--selected' : ''}`}
          >
            <span className="cp-stories-card__accent" style={{ backgroundColor: city.tone }} />
            <span
              className="cp-stories-card__glow pointer-events-none"
              style={{ backgroundColor: city.tone }}
              aria-hidden
            />

            <div className="cp-stories-card__body">
              <div className="cp-stories-card__head">
                <span
                  className="cp-stories-card__avatar"
                  style={{ background: `linear-gradient(145deg, ${city.tone}, ${city.tone}99)` }}
                >
                  {city.city.slice(0, 1)}
                </span>
                <div className="min-w-0 flex-1 text-right">
                  <span className="cp-stories-card__badge">
                    <BadgeCheck className="h-3 w-3" />
                    همراه تأیید‌شده
                  </span>
                  <h3 className="cp-stories-card__title">
                    {city.city}
                    <span className="cp-stories-card__country"> · {city.country}</span>
                  </h3>
                  <p className="cp-stories-card__en">{getGlobeCityLabel(city.id)}</p>
                </div>
                <MapPin className="cp-stories-card__pin" />
              </div>

              <Quote className="cp-stories-card__quote" aria-hidden />

              <p className="cp-stories-card__story">{city.story}</p>

              <div className="cp-stories-card__footer">
                <span className="cp-stories-card__highlight">{city.highlight}</span>
                <span className="cp-stories-card__companions">+{formatFa(city.companions)}</span>
              </div>

              <div className="cp-stories-card__actions">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenVideo(city);
                  }}
                  className="cp-stories-card__video"
                >
                  <PlayCircle className="h-3.5 w-3.5" />
                  ویدیو
                </button>
                <span className="cp-stories-card__select">
                  انتخاب
                  <ArrowLeft className="h-3.5 w-3.5 -scale-x-100" />
                </span>
              </div>
            </div>
          </motion.article>
        );
      })}
    </div>
  );
};

export default CompanionsCityGrid;
